import { CommonModule } from '@angular/common'
import { Component, OnInit, OnDestroy } from '@angular/core'
import { MatDialogRef } from '@angular/material/dialog'
import { PaginationComponent } from '../../../inbox/common/pagination/pagination.component'
import { FormsModule } from '@angular/forms'
import { NgSelectModule } from '@ng-select/ng-select'
import { LoadingDirective } from 'src/app/directive/loader.directive'
import { NotificationService } from '../services/notification.service'
import {
    Notification,
    NotificationListResponse,
} from '../model/notification.model'
import { Subject, takeUntil } from 'rxjs'

interface DisplayNotification {
    id: string
    type: 'incoming_sms' | 'incoming_email' | 'follow'
    user: {
        name: string
        avatar: string
    }
    message: string
    patientId: string
    subject?: string
    timeAgo: string
    read: boolean
    hasMMSAttachment:boolean;
}

interface DropdownOption {
    label: string
    value: string
}

@Component({
    selector: 'app-view-all-notification-modal',
    standalone: true,
    imports: [
        CommonModule,
        PaginationComponent,
        FormsModule,
        NgSelectModule,
        LoadingDirective,
    ],
    templateUrl: './view-all-notification-modal.component.html',
    styleUrls: ['./view-all-notification-modal.component.css'],
})
export class ViewAllNotificationModalComponent implements OnInit, OnDestroy {
    pageObject: { pageNum: number; selected: boolean }[] = []
    paginationConfig = { id: 'customPagination', currentPage: 1 }
    totalItems = 0
    itemsPerPage = 3
    notifications: DisplayNotification[] = []
    isLoading = false
    private destroy$ = new Subject<void>() 

    viewingOptions: DropdownOption[] = [
        { label: 'All', value: 'all' },
        { label: 'Unread', value: 'unread' },
    ]
    selectedViewing: string = 'all'

    sortOptions: DropdownOption[] = [
        { label: 'Newest', value: 'newest' },
        { label: 'Oldest', value: 'oldest' },
    ]
    selectedSort: string = 'newest'

    constructor(
        public dialogRef: MatDialogRef<ViewAllNotificationModalComponent>,
        private notificationService: NotificationService,
    ) {}

    ngOnInit(): void {
        this.getAllNotifications(1)
    }

    private mapToDisplayNotifications(
        apiNotifications: Notification[],
    ): DisplayNotification[] {
        return apiNotifications.map((n) => ({
            id: n.id,
            type: n.type,
            user: {
                name: n.patientName,
                avatar: n.patientProfileUrl,
            },
            message: n.content,
            patientId: n.patientId,
            subject: n.emailSubject ?? undefined,
            timeAgo: this.calculateTimeAgo(n.createdDateTimeUtc),
            read: n.isRead === 1,
            hasMMSAttachment: n.hasMMSAttachment
        }))
    }

    private calculateTimeAgo(date: string): string {
        const now = new Date()
        const notificationDate = new Date(date)
        const diffMs = now.getTime() - notificationDate.getTime()
        const diffMins = Math.round(diffMs / 60000)
        if (diffMins < 60)
            return `${diffMins} min${diffMins !== 1 ? '' : ''} ago`
        const diffHours = Math.round(diffMins / 60)
        if (diffHours < 24)
            return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
        const diffDays = Math.round(diffHours / 24)
        return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
    }

    private initializePagination(totalItems: number): void {
        const pageCount = Math.ceil(totalItems / this.itemsPerPage)
        this.pageObject = Array.from({ length: pageCount }, (_, i) => ({
            pageNum: i + 1,
            selected: i === this.paginationConfig.currentPage - 1,
        }))
    }

    onCancel(): void {
        this.dialogRef.close()
    }

    onPageChange(newPage: number): void {
        this.paginationConfig.currentPage = newPage
        this.pageObject = this.pageObject.map((page) => ({
            ...page,
            selected: page.pageNum === newPage,
        }))
        this.getAllNotifications(newPage)
    }

    getAllNotifications(pageNumber: number): void {
        this.isLoading = true
        const params = {
            page: pageNumber,
            limit: this.itemsPerPage,
            sort: this.selectedSort as 'newest' | 'oldest',
            view: this.selectedViewing as 'all' | 'unread',
        }

        this.notificationService
            .getNotifications(params)
            .pipe(takeUntil(this.destroy$)) 
            .subscribe({
                next: (response: NotificationListResponse) => {
                    this.notifications = this.mapToDisplayNotifications(
                        response.data,
                    )
                    this.totalItems = response.meta.totalCount
                    this.initializePagination(response.meta.totalCount)
                    this.isLoading = false
                },
                error: (error) => {
                    console.error('Error fetching notifications:')
                    this.isLoading = false
                    this.notifications = []
                    this.totalItems = 0
                    this.initializePagination(0)
                },
            })
    }

    refreshNotifications(): void {
        this.getAllNotifications(1)
    }

    onViewingChange(selectedOption: DropdownOption): void {
        this.selectedViewing = selectedOption.value
        this.paginationConfig.currentPage = 1
        this.getAllNotifications(1)
    }

    onSortChange(selectedOption: DropdownOption): void {
        this.selectedSort = selectedOption.value
        this.paginationConfig.currentPage = 1
        this.getAllNotifications(1)
    }

    openMyConversation(notificationId: string): void {
        this.notificationService.openMyConversation(notificationId)
        this.dialogRef.close()
    }

    ngOnDestroy(): void {
        this.destroy$.next() 
        this.destroy$.complete() 
    }
}
